import * as THREE from 'three';

/**
 * The loading screen, and the work it exists to cover.
 *
 * It is not decoration. A browser game does most of its expensive one-off work
 * lazily — shader programs are built the first time a material is drawn, textures
 * upload to the GPU the first time they are sampled, and both are main-thread
 * stalls that land exactly when something new appears on screen. Left alone that
 * is a game which hitches for the first twenty seconds of every match and then
 * runs perfectly, which is the worst possible distribution: it is at its
 * roughest when the player is deciding what they think of it.
 *
 * So the screen does the work rather than waiting through it. Each phase forces
 * something that would otherwise happen mid-fight, and yields to the browser
 * between phases so the progress bar actually moves.
 */

export interface Phase {
  label: string;
  run: () => void | Promise<void>;
}

export class LoadingScreen {
  private readonly root: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly note: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    root.hidden = false;
    root.innerHTML =
      '<div class="loading-sheet">'
      + '<div class="loading-eyebrow">The Cathedral</div>'
      + '<h1 class="loading-title">Preparing</h1>'
      + '<div class="loading-track"><div class="loading-bar"></div></div>'
      + '<p class="loading-note"></p>'
      + '</div>';
    this.bar = root.querySelector('.loading-bar') as HTMLElement;
    this.note = root.querySelector('.loading-note') as HTMLElement;
  }

  /**
   * Runs each phase, painting between them.
   *
   * The double `requestAnimationFrame` is deliberate: one frame to let the
   * browser lay out the new label, a second to let it actually paint before the
   * main thread disappears into the next phase. With a single yield the bar
   * jumps from empty to full at the end, having described nothing.
   */
  async run(phases: readonly Phase[]): Promise<void> {
    for (let i = 0; i < phases.length; i++) {
      this.note.textContent = phases[i].label;
      this.bar.style.width = `${(i / phases.length) * 100}%`;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await phases[i].run();
    }
    this.bar.style.width = '100%';
    this.note.textContent = 'Ready';
    await new Promise((r) => setTimeout(r, 120));
  }

  dismiss(): void {
    this.root.hidden = true;
    this.root.innerHTML = '';
  }
}

/**
 * Uploads every texture in a scene to the GPU.
 *
 * `compileAsync` builds shader programs but does not necessarily upload the
 * textures they sample; the first draw that needs one stalls while it goes
 * across. Thirteen textures on a mage is thirteen chances to hitch.
 */
export function uploadTextures(renderer: THREE.WebGLRenderer, scene: THREE.Object3D): number {
  const seen = new Set<THREE.Texture>();
  scene.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      const m = material as unknown as Record<string, unknown>;
      for (const key of Object.keys(m)) {
        const value = m[key];
        if (value && (value as THREE.Texture).isTexture) seen.add(value as THREE.Texture);
      }
    }
  });
  for (const texture of seen) renderer.initTexture(texture);
  return seen.size;
}

/**
 * Draws real frames so the passes `compileAsync` cannot reach get built.
 *
 * Two of them matter. The **shadow pass** renders with its own depth materials,
 * which are separate programs from the ones a scene compile touches — so the
 * first frame with shadows on stalls however thoroughly the scene was prepared.
 * And the **composer** builds its fullscreen quad programs on first use, which
 * is bloom's entire cost paid at the worst moment.
 *
 * A handful of frames covers both, and nobody sees them: the canvas is behind
 * the loading screen.
 */
export function warmFrames(draw: () => void, count = 3): void {
  for (let i = 0; i < count; i++) draw();
}
